import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { i18nService } from '../../services/i18n.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LanguageTabs from '../../components/common/LanguageTabs';
import toast from 'react-hot-toast';
import { FaPlus, FaEdit, FaTrash, FaFolder } from 'react-icons/fa';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentLang, setCurrentLang] = useState('nl');

  const [formData, setFormData] = useState({
    name: { nl: '', fr: '', de: '', en: '' },
    order: 1
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await databaseService.getGalleryCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to load categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least one language has content
    if (!i18nService.hasContent(formData.name)) {
      toast.error('Category name is required in at least one language');
      return;
    }

    setIsSaving(true);
    try {
      const categoryData = {
        ...i18nService.setTranslatedFields('name', formData.name),
        order: Number(formData.order)
      };

      if (selectedCategory) {
        await databaseService.update(`website/gallery/categories/${selectedCategory.id}`, categoryData);
        toast.success('Category updated successfully');
      } else {
        await databaseService.create('website/gallery/categories', categoryData);
        toast.success('Category added successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save category');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      name: i18nService.extractMultiLangField(category, 'name'),
      order: category.order || 1
    });
    setCurrentLang('nl');
    setShowAddModal(true);
  };

  const handleDelete = async () => {
    try {
      // Check if category is being used
      const items = await databaseService.getAllGalleryItems();
      const itemsInCategory = items.filter(item => item.category === selectedCategory.name);

      if (itemsInCategory.length > 0) {
        toast.error(`Cannot delete category. ${itemsInCategory.length} item(s) are using this category.`);
        setShowDeleteDialog(false);
        return;
      }

      await databaseService.delete(`website/gallery/categories/${selectedCategory.id}`);
      toast.success('Category deleted successfully');
      setShowDeleteDialog(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: { nl: '', fr: '', de: '', en: '' },
      order: categories.length + 1
    });
    setCurrentLang('nl');
    setSelectedCategory(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gallery Categories</h1>
            <p className="text-gray-600">Manage categories for your gallery items</p>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          >
            <FaPlus className="mr-2" /> Add Category
          </Button>
        </div>

        {/* Categories List */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display Order
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center text-gray-500">
                      <FaFolder className="mx-auto text-gray-400 text-5xl mb-3" />
                      <p>No categories yet. Add your first category to organize your gallery.</p>
                    </td>
                  </tr>
                ) : (
                  categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaFolder className="text-primary-600 mr-3" />
                          <span className="text-sm font-medium text-gray-900">
                            {i18nService.getFirstAvailable(i18nService.extractMultiLangField(category, 'name'), 'nl') || category.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{category.order}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(category)}
                          className="text-blue-600 hover:text-blue-800 mr-4"
                        >
                          <FaEdit className="inline mr-1" /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash className="inline mr-1" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">ℹ️ About Categories</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Categories help organize your gallery items (photos and videos)</li>
            <li>• They appear as filter buttons on the public gallery page</li>
            <li>• Display order controls the sequence of filter buttons</li>
            <li>• You cannot delete a category if it has gallery items assigned to it</li>
          </ul>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        title={selectedCategory ? 'Edit Category' : 'Add Category'}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <LanguageTabs
              currentLang={currentLang}
              onLangChange={setCurrentLang}
            />
          </div>

          <Input
            label={`Category Name (${currentLang.toUpperCase()})`}
            value={formData.name[currentLang]}
            onChange={(e) => setFormData({
              ...formData,
              name: { ...formData.name, [currentLang]: e.target.value }
            })}
            placeholder="e.g., Weddings, Birthday Parties, Corporate Events"
            helperText="Enter category name in the selected language"
          />

          <Input
            label="Display Order"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
            required
            min="1"
            helperText="Lower numbers appear first in the filter list"
          />

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSaving} disabled={isSaving}>
              {isSaving ? 'Saving...' : selectedCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </AdminLayout>
  );
};

export default CategoriesManager;
